/* eslint-disable id-length */
import classNames from "classnames";
import debounce from "lodash/debounce";
import { useCallback, useRef, useState } from "react";
import BuildAnnotations from "~/components/build/Show/components/annotations/BuildAnnotations";
import JobAnnotations from "~/components/build/Show/components/annotations/JobAnnotations";
import { ResizeHandle } from "~/components/build/Show/components/ResizeHandle";
import { useBuild } from "~/components/build/Show/lib/BuildContext";
import { getAnnotationCounts } from "~/components/build/Show/lib/annotationCounts";
import {
  clampAnnotationsPanelWidth,
  useBuildPreferencesStore,
} from "~/components/build/Show/lib/useBuildPreferencesStore";
import { AnnotationsTab } from "~/components/build/Show/lib/types";

const RESIZE_DEBOUNCE_WAIT = 150;
const DEFAULT_TAB = AnnotationsTab.Build;

const TAB_LABELS: Record<AnnotationsTab, string> = {
  [AnnotationsTab.Build]: "Build",
  [AnnotationsTab.Jobs]: "Jobs",
};

function AnnotationsPanelTab({
  tab,
  isActive,
  count,
  onSelect,
}: {
  tab: AnnotationsTab;
  isActive: boolean;
  count: number;
  onSelect: (tab: AnnotationsTab) => void;
}) {
  const handleClick = useCallback(() => onSelect(tab), [onSelect, tab]);
  return (
    <button
      type="button"
      onClick={handleClick}
      className={classNames(
        "rounded-sm px-2 py-1 text-xs font-normal leading-5 transition-colors duration-200",
        isActive
          ? "bg-[#e5e7eb]/60 text-[#030712]"
          : "text-[#4b5563] hover:bg-[#e5e7eb]/60 hover:text-[#111827]",
      )}
    >
      {TAB_LABELS[tab]}
      {count > 0 && <span className="ml-1 text-[11px] tabular-nums text-[#6b7280]">{count}</span>}
    </button>
  );
}

function AnnotationsPanelContent({ activeTab }: { activeTab: AnnotationsTab }) {
  switch (activeTab) {
    case AnnotationsTab.Build:
      return <BuildAnnotations />;
    case AnnotationsTab.Jobs:
      return <JobAnnotations />;
  }
}

export default function AnnotationsPanel() {
  const { build } = useBuild();
  const annotationCounts = getAnnotationCounts(build);
  const [activeTab, setActiveTab] = useState<AnnotationsTab>(DEFAULT_TAB);

  const width = useBuildPreferencesStore((state) => state.annotationsPanelWidth);
  const setAnnotationsPanelWidth = useBuildPreferencesStore(
    (state) => state.setAnnotationsPanelWidth,
  );

  const panelRef = useRef<HTMLDivElement>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const persistWidth = useCallback(
    debounce((value: number) => setAnnotationsPanelWidth(value), RESIZE_DEBOUNCE_WAIT),
    [],
  );

  const resize = useCallback(
    (x: number) => {
      if (!panelRef.current) {
        return;
      }
      const rect = panelRef.current.getBoundingClientRect();
      const nextWidth = clampAnnotationsPanelWidth(rect.right - x);
      panelRef.current.style.width = `${nextWidth}px`;
      persistWidth(nextWidth);
    },
    [persistWidth],
  );

  return (
    <div
      ref={panelRef}
      data-testid="AnnotationsPanel"
      style={{ width }}
      className="relative flex h-full min-w-0 flex-auto flex-col rounded-md border border-gray-400 bg-white md:flex-initial"
    >
      <ResizeHandle direction="horizontal" onResize={resize} className="left-[1px] rounded-l-md" />

      <div className="flex flex-auto flex-col">
        <div className="flex w-full items-center border-b border-gray-400 px-2.5">
          <div className="flex items-center gap-0.5 py-1.5">
            {Object.values(AnnotationsTab).map((tab) => (
              <AnnotationsPanelTab
                key={tab}
                tab={tab}
                isActive={tab === activeTab}
                count={annotationCounts[tab]}
                onSelect={setActiveTab}
              />
            ))}
          </div>
        </div>

        <div className="relative flex-auto">
          <div className="absolute flex h-full w-full overflow-y-auto">
            <div className="relative flex w-full flex-1 flex-col gap-2 p-2.5">
              <AnnotationsPanelContent activeTab={activeTab} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
